import { nanoid } from "nanoid";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { grantsForPrincipal } from "../rbac/grantQueries";
import { normalizeName } from "../normalizeName";
import { assertAccess } from "../rbac/access";
import { type BuiltinRoleId } from "../rbac/roles";
import {
  assertBuiltinRoleScope,
  assertCustomRoleScope,
  assertIdpSyncNotActive,
} from "../rbac/validation";
import { userEmailSchema } from "../userFields";
import { findUserByEmail } from "../userQueries";
import { mutator } from "./mutator";

const inviteOrReactivateUserGroupGrant = z.object({
  userGroupId: z.string(),
  builtinRoleId: z.string().optional(),
  customRoleId: z.string().optional(),
});

export const inviteOrReactivateUserSchema = z.object({
  id: z.string().min(1),
  email: userEmailSchema,
  groupGrants: z.array(inviteOrReactivateUserGroupGrant).optional(),
});

export const inviteOrReactivateUser = mutator({
  schema: inviteOrReactivateUserSchema,
  resolve: {
    groups: {
      kind: "userGroup",
      from: "groupGrants",
      field: "userGroupId",
    },
    groupCustomRoles: {
      kind: "role",
      from: "groupGrants",
      field: "customRoleId",
    },
  },
  permissions: [
    { permission: "tenant:users_manage", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({ tx, ctx, args }) => {
    await assertIdpSyncNotActive(tx, ctx.tenantId, "userProvisioning");

    const groupGrants = args.groupGrants ?? [];
    for (const grant of groupGrants) {
      if (!!grant.builtinRoleId === !!grant.customRoleId) {
        throw userFacingError(
          "Each group grant must specify exactly one of builtinRoleId or customRoleId"
        );
      }
      if (grant.builtinRoleId) {
        assertBuiltinRoleScope(grant.builtinRoleId, "user_group");
      }
    }

    for (const customRole of args.groupCustomRoles) {
      assertCustomRoleScope(customRole, "user_group");
    }

    const uniqueGroupIds = [...new Set(groupGrants.map((g) => g.userGroupId))];
    for (const groupId of uniqueGroupIds) {
      await assertAccess({
        tx,
        userId: ctx.userId,
        // Reachable via the public createUser route, whose ctx can be a
        // service account principal; check it rather than the
        // audit-attribution userId.
        principal: ctx.principal,
        scopeId: groupId,
        tenantId: ctx.tenantId,
        permission: "user_group:write",
      });
    }

    const existingUser = await findUserByEmail(tx, ctx.tenantId, args.email);

    let userId: string;

    if (existingUser) {
      if (!existingUser.deactivated) {
        throw userFacingError("A user with this email already exists");
      }
      await tx.mutate.user.update({
        id: existingUser.id,
        deactivated: false,
        emailVerified: true,
      });

      const allGroups = await tx.run(
        zql.userGroups.where("tenantId", ctx.tenantId)
      );
      const groupIds = new Set(allGroups.map((g) => g.id));
      const existingGrants = await tx.run(
        grantsForPrincipal({ userId: existingUser.id }, ctx.tenantId)
      );
      for (const grant of existingGrants) {
        if (groupIds.has(grant.scopeId)) {
          await tx.mutate.roleGrants.delete({ id: grant.id });
        }
      }

      userId = existingUser.id;
    } else {
      const now = Date.now();

      await tx.mutate.user.insert({
        id: args.id,
        email: args.email,
        emailVerified: true,
        name: normalizeName(""),
        tenantId: ctx.tenantId,
        invitedAt: now,
        createdAt: now,
        updatedAt: now,
      });

      userId = args.id;
    }

    for (const grant of groupGrants) {
      await tx.mutate.roleGrants.insert({
        id: nanoid(12),
        userId,
        builtinRoleId: grant.builtinRoleId as BuiltinRoleId | undefined,
        customRoleId: grant.customRoleId,
        scopeId: grant.userGroupId,
        createdAt: Date.now(),
        tenantId: ctx.tenantId,
      });
    }
  },
});
